import type { Action } from 'modules/global/generic.types';

import { uniq } from './ObjectUtils';

type ReducerHandler<U, A = never> = (state: U, action: A) => U;
type ReducerMatcher<U> = {
  matcher: (action: Action, state?: U) => boolean;
  reducer: ReducerHandler<U>;
};

function createReducer<U>(
  initialState: U,
  handlers: Record<string, ReducerHandler<U>>,
  matchers: ReducerMatcher<U>[] = []
): (state: U | undefined, action: Action) => U {
  // eslint-disable-next-line default-param-last
  return function reducer(state = initialState, action: Action): U {
    // Handlers and matchers declare the action type THEY handle (their param is `never`, the
    // contravariant way of saying "the caller knows"), so the dispatch point is the one place that
    // has to widen — instead of every handler in the codebase taking an `any`.
    if (handlers?.[action.type]) {
      return handlers[action.type](state ?? initialState, action as never);
    }

    for (const { matcher, reducer: matcherReducer } of matchers) {
      if (matcher(action, state)) {
        return matcherReducer(state, action as never);
      }
    }
    return state;
  };
}

const sourceMatcher = (source: any, fn: any) => (state: any, action: any) => {
  if (action.meta?.source === source) {
    return fn(state, action);
  }
  return state;
};

export type EntitySlice<T> = {
  ids: string[];
  entities: Record<string, T>;
};

export type EntityAdapter<T> = {
  getInitialState: () => EntitySlice<T>;
  addOne: (state: EntitySlice<T>, entityOrAction: Partial<T> | Action<Partial<T>>) => EntitySlice<T>;
  addMany: (state: EntitySlice<T>, entitiesOrAction: Partial<T>[] | Action<Partial<T>[]>) => EntitySlice<T>;
  setAll: (state: EntitySlice<T>, entitiesOrAction: Partial<T>[] | Action<Partial<T>[]>) => EntitySlice<T>;
  removeOne: (state: EntitySlice<T>, entityIdOrAction: string | Action<string>) => EntitySlice<T>;
  removeMany: (state: EntitySlice<T>, entityIdsOrAction: string[] | Action<string[]>) => EntitySlice<T>;
  removeAll: () => EntitySlice<T>;
  updateOne: (state: EntitySlice<T>, entityOrAction: Partial<T> | Action<Partial<T>>) => EntitySlice<T>;
  updateMany: (state: EntitySlice<T>, entitiesOrAction: Partial<T>[] | Action<Partial<T>[]>) => EntitySlice<T>;
};

const extractPayload = <V>(actionOrValue: V | Action<V>): V =>
  (((actionOrValue as Action<V>).payload ?? actionOrValue) as V);

const createEntityAdapter = <T>({ selectId }: { selectId: (entity: T) => string }): EntityAdapter<T> => {
  const getInitialState = (): EntitySlice<T> => ({
    ids: [],
    entities: {}
  });

  const addOneRaw = (state: EntitySlice<T>, entity: Partial<T>): EntitySlice<T> => {
    const entityId = selectId(entity as T);
    return {
      entities: { ...state.entities, [entityId]: entity as T },
      ids: uniq([...state.ids, entityId])
    } as EntitySlice<T>;
  };

  const addOne: EntityAdapter<T>['addOne'] = (state, entityOrAction) => {
    const entity = extractPayload<Partial<T>>(entityOrAction);
    return addOneRaw(state, entity);
  };

  const addMany: EntityAdapter<T>['addMany'] = (state, entitiesOrAction) => {
    const entities = extractPayload<Partial<T>[]>(entitiesOrAction);
    return entities.reduce(addOneRaw, state);
  };

  const setAll: EntityAdapter<T>['setAll'] = (_, entitiesOrAction) => {
    const entities = extractPayload<Partial<T>[]>(entitiesOrAction);
    return entities.reduce(addOneRaw, getInitialState());
  };

  const removeOne: EntityAdapter<T>['removeOne'] = (state, entityIdOrAction) => {
    const entityId = extractPayload<string>(entityIdOrAction);
    const { [entityId]: _omitted, ...entities } = state.entities;
    return {
      entities,
      ids: state.ids.filter((id) => id !== entityId)
    };
  };

  const removeMany: EntityAdapter<T>['removeMany'] = (state, entityIdsOrAction) => {
    const entityIds = extractPayload<string[]>(entityIdsOrAction);
    return entityIds.reduce(removeOne, state);
  };

  const removeAll = (): EntitySlice<T> => getInitialState();

  const updateOneRaw = (state: EntitySlice<T>, entity: Partial<T>): EntitySlice<T> => {
    const entityId = selectId(entity as T);
    return {
      entities: {
        ...state.entities,
        [entityId]: { ...(state.entities[entityId] ?? {}), ...entity } as T
      },
      ids: uniq([...state.ids, entityId])
    } as EntitySlice<T>;
  };

  const updateOne: EntityAdapter<T>['updateOne'] = (state, entityOrAction) => {
    const entity = extractPayload<Partial<T>>(entityOrAction);
    return updateOneRaw(state, entity);
  };

  const updateMany: EntityAdapter<T>['updateMany'] = (state, entitiesOrAction) => {
    const entities = extractPayload<Partial<T>[]>(entitiesOrAction);
    return entities.reduce(updateOneRaw, state);
  };

  return { getInitialState, addOne, addMany, setAll, removeOne, removeMany, removeAll, updateOne, updateMany };
};

export { createReducer, sourceMatcher, createEntityAdapter };
